import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { sectors } from "@/lib/data/sectors";
import useSEO from "@/hooks/useSEO";

export default function Sectors() {
  useSEO({
    title:
      "Sector Landing Pages — Service × City | Manitoba Security Services",
    description:
      "Hyper-specific security programs matched to industry and community: mining security in Thompson, oil and gas security in Melita, event security in Winnipeg, and more.",
    keywords: sectors.flatMap((s) => s.keywords),
    canonical:
      typeof window !== "undefined" ? window.location.origin + "/sectors" : "/sectors",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Sector Programs — Manitoba Security Services",
      itemListElement: sectors.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: s.heading,
        url: `/sectors/${s.slug}`,
      })),
    },
  });

  return (
    <div className="bg-[#050505] pt-32 md:pt-40">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            Index — 05
          </p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1
            data-testid="sectors-title"
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
          >
            Sector{" "}
            <span className="font-editorial italic font-normal text-[#a0a0a0]">
              programs
            </span>
          </h1>
          <p className="mt-8 font-body text-base md:text-lg text-[#a0a0a0] max-w-2xl leading-relaxed">
            Curated programs that match a specific discipline to a specific
            Manitoba community — mining security in Thompson, oil and gas
            coverage in Melita, event security in Winnipeg. Each program is
            staffed and operated as a distinct operating unit, not a generic
            deployment.
          </p>
        </div>
      </section>

      <section className="border-t border-[#1e1e1e]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          {sectors.map((s, i) => (
            <motion.div
              key={s.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.7,
                delay: i * 0.03,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <Link
                to={`/sectors/${s.slug}`}
                data-testid={`sectors-list-${s.slug}`}
                className="group grid grid-cols-12 gap-4 items-baseline border-b border-[#1e1e1e] py-8 md:py-10 hover:border-[#FF3B30] transition-colors duration-500"
              >
                <span className="col-span-2 md:col-span-1 font-editorial italic text-[#6a6a6a] group-hover:text-[#FF3B30] transition-colors text-xl md:text-2xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="col-span-8 md:col-span-9">
                  <h2 className="font-display font-black uppercase tracking-tight text-white text-xl md:text-3xl lg:text-4xl leading-tight group-hover:translate-x-2 transition-transform duration-500">
                    {s.heading}
                  </h2>
                  <p className="mt-3 font-editorial italic text-[#a0a0a0] text-base md:text-lg max-w-2xl">
                    {s.lede}
                  </p>
                </div>
                <span className="col-span-2 flex justify-end">
                  <ArrowUpRight
                    className="text-white/60 group-hover:text-[#FF3B30] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500"
                    size={26}
                  />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
